import PlatformApi, { type IEvent } from 'clients/PlatformApi';
import { useCallback, useState } from 'react';
import useLocale from 'src/hooks/useLocale';
import * as EventUtil from 'utils/EventUtil';
import LocaleUtil from 'utils/LocaleUtil';
import { FAST_NOTIFICATION_DURATION } from 'vd-constants';

export interface ArchivePanelProps {
  archivePanelOpen?: boolean;
  closeArchivePanel?: () => void;
  event: IEvent;
  type: 'archive' | 'unarchive';
  additionalArchiveActions?: () => void;
  additionalUnarchiveActions?: () => void;
  removeEvent?: (targetEvent: IEvent) => void;
}

export interface ArchiveNotifier {
  success: (message: string, options?: { autoClose?: number }) => void;
  error: (message: string) => void;
}

export function useArchivePanelActions(props: ArchivePanelProps, notify: ArchiveNotifier) {
  const {
    event,
    type,
    closeArchivePanel,
    additionalArchiveActions,
    additionalUnarchiveActions,
    removeEvent,
  } = props;
  const [isLoading, setIsLoading] = useState(false);
  const locale = useLocale();

  const isArchiveContext = type === 'archive';

  const modalCloseAction = useCallback(() => {
    closeArchivePanel?.();
  }, [closeArchivePanel]);

  const archiveEvent = useCallback(async () => {
    const { title, id, shares } = event;
    setIsLoading(true);
    try {
      await PlatformApi().bulkArchiveEvents([
        { id, shares: EventUtil.getEventClosedShares(shares) },
      ]);

      notify.success(
        LocaleUtil.getLocalizedStr({
          string: locale.archive.archiveNotificationMessage,
          replacements: { title },
        }),
      );

      additionalArchiveActions?.();
      removeEvent?.(event);
      closeArchivePanel?.();
      return true;
    } catch (error) {
      console.error(error);
      notify.error(locale.archive.errorToast);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [
    additionalArchiveActions,
    closeArchivePanel,
    event,
    locale.archive.archiveNotificationMessage,
    locale.archive.errorToast,
    notify,
    removeEvent,
  ]);

  const unarchiveEvent = useCallback(async () => {
    const { title, id } = event;
    setIsLoading(true);
    try {
      await PlatformApi().unarchiveEvent(id);
      notify.success(
        LocaleUtil.getLocalizedStr({
          string: locale.archive.unarchiveNotificationMessage,
          replacements: { title },
        }),
        { autoClose: FAST_NOTIFICATION_DURATION },
      );

      additionalUnarchiveActions?.();
      removeEvent?.(event);
      closeArchivePanel?.();
      return true;
    } catch (error) {
      console.error(error);
      notify.error(locale.archive.errorToast);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [
    additionalUnarchiveActions,
    closeArchivePanel,
    event,
    locale.archive.errorToast,
    locale.archive.unarchiveNotificationMessage,
    notify,
    removeEvent,
  ]);

  const isShare = EventUtil.isShared(event);
  const isDownload = EventUtil.isDownload(event);
  const isClip = isShare || isDownload;
  const eventType = isClip ? locale.archive.clip : locale.archive.event;
  const archiveAction = isArchiveContext ? locale.archive.archive : locale.archive.unarchive;

  const archiveConfirmation = LocaleUtil.getLocalizedStr({
    string: locale.archive.archiveConfirmation,
    replacements: {
      eventType: eventType.toLowerCase(),
      archiveAction: archiveAction.toLowerCase(),
    },
  });

  const modalTitle = LocaleUtil.getLocalizedStr({
    string: locale.archive.archiveTitle,
    replacements: { eventType, archiveAction },
  });

  let infoText: string | undefined;

  if (isShare) {
    if (isArchiveContext) {
      infoText = locale.archive.archiveClipInfoText;
    } else {
      infoText = locale.archive.unarchiveClipInfoText;
    }
  }

  return {
    isLoading,
    modalCloseAction,
    onConfirm: isArchiveContext ? archiveEvent : unarchiveEvent,
    modalTitle,
    archiveConfirmation,
    infoText,
    eventTitle: event.title,
    cancelLabel: locale.general.cancel,
    confirmLabel: locale.general.confirm,
  };
}
